import { Injectable, NgZone } from '@angular/core';
import * as pako from 'pako';
import { environment } from 'environments/environment';
import { ApiService } from '../../../sse.service';
import { StreamService } from 'app/company/tracker/new-tracker/stream.service';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';

export interface TableUploadDoneInfo {
    /** Technical table name from the progress stream (preferred for generate-table-desc). */
    tableName?: string;
}

export interface TableUploadProgressOptions {
    onProgress: (v: number) => void;
    onStatusChange?: (status: string) => void;
    onFinalizingChange: (f: boolean) => void;
    onDone: (info?: TableUploadDoneInfo) => void;
    onError: (e: any) => void;
}

@Injectable({ providedIn: 'root' })
export class TableUploadService {
    constructor(
        private apiService: ApiService,
        private streamService: StreamService,
        private zone: NgZone,
        private alert: CustomAlertService
    ) {}

    compressToFormData(payload: any, fileSizeKB: number): FormData {
        const gz = pako.gzip(JSON.stringify(payload));
        const file = new File(
            [new Blob([gz], { type: 'application/gzip' })],
            'data.gz'
        );
        const fd = new FormData();
        fd.append('file', file);
        fd.append('size', JSON.stringify(fileSizeKB));
        return fd;
    }

    /** Returns the same gzip File that would be sent in FormData (for download). */
    getGzipFile(payload: any, fileSizeKB: number): File {
        const gz = pako.gzip(JSON.stringify(payload));
        return new File(
            [new Blob([gz], { type: 'application/gzip' })],
            'data.gz'
        );
    }

    upload(
        formData: FormData,
        opts: TableUploadProgressOptions
    ): Promise<TableUploadDoneInfo> {
        const formDataLog: Record<string, unknown> = {};
        const fd = formData as unknown as { entries(): IterableIterator<[string, string | File]> };
        if (typeof fd.entries === 'function') {
            for (const [key, value] of fd.entries()) {
                const v = value as unknown;
                if (v instanceof File || (v && typeof (v as { size?: number }).size === 'number')) {
                    const f = v as { type: string; size: number; name?: string };
                    formDataLog[key] = { type: f.type, size: f.size, name: f.name || '(blob)' };
                } else {
                    formDataLog[key] = value;
                }
            }
        }
        return new Promise((resolve, reject) => {
            let settled = false;
            let streamTableName = '';
            const finish = (fn: () => void) => {
                if (settled) {
                    return;
                }
                settled = true;
                fn();
            };
            const doneInfo = (): TableUploadDoneInfo =>
                streamTableName ? { tableName: streamTableName } : {};

            /**
             * V2 for every environment, deliberately. This used to read
             * `environment.production ? v1 : v2`, which pinned production to the V1 route
             * while only dev got the streaming import. V1 holds the whole decompressed
             * payload in the API process (read + JSON.parse), so a large upload pushed the
             * process past its PM2 memory ceiling and PM2 restarted it mid-import; the user
             * saw the upload fail and retried, which did it again. V2 keeps the rows on disk
             * and streams them, so peak memory tracks the insert batch, not the file.
             *
             * `production` is a build mode, not a feature switch (see environment.ts).
             * There is no reason for the two environments to take different upload paths:
             * the behaviour they must exhibit is identical, so they run the same code.
             */
            this.apiService
                .postFormData(`${environment.apiLink}data_source/upload-internal-table-v2`, formData)
                .subscribe({
                    next: (res) => {
                        let jobId = '';
                        try {
                            const parsed = JSON.parse(res);
                            jobId =
                                parsed?.data?.jobId ??
                                parsed?.jobId ??
                                '';
                            jobId = jobId ? String(jobId) : '';
                        } catch {}

                        if (!jobId) {
                            opts.onDone(doneInfo());
                            finish(() => resolve(doneInfo()));
                            return;
                        }

                        const token = localStorage.getItem('accessToken');
                        if (!token) {
                            const err = 'Missing access token';
                            opts.onError(err);
                            finish(() => reject(err));
                            return;
                        }

                        const url = `${environment.apiLink}data_source/upload-internal-table-progress?token=${encodeURIComponent(token)}&jobId=${encodeURIComponent(jobId)}`;
                        /** Listen for named SSE events; backend may emit final outcome as `done` or `success`. */
                        const events = [
                            'progress',
                            'status',
                            'error',
                            'done',
                            'success',
                        ];

                        this.zone.runOutsideAngular(() => {
                            const sub = this.streamService
                                .connectToServerSentEvents(
                                    url,
                                    { withCredentials: false },
                                    events
                                )
                                .subscribe({
                                    next: (ev: any) => {
                                        const data = ev?.data ?? {};

                                        const nextTableName = String(
                                            data['tableName'] ?? ''
                                        ).trim();
                                        if (nextTableName) {
                                            streamTableName = nextTableName;
                                        }

                                        if (data['progress'] != null) {
                                            const v = Math.ceil(
                                                Number(data['progress'])
                                            );
                                            this.zone.run(() =>
                                                opts.onProgress(v)
                                            );
                                        }
                                        if (data['status']) {
                                            const status = String(
                                                data['status']
                                            );
                                            if (status === 'finalizing') {
                                                this.zone.run(() =>
                                                    opts.onFinalizingChange(true)
                                                );
                                            } else if (opts.onStatusChange) {
                                                this.zone.run(() =>
                                                    opts.onStatusChange(status)
                                                );
                                            }
                                        }

                                        if (data['error']) {
                                            this.zone.run(() => {
                                                opts.onFinalizingChange(false);
                                                const msg =
                                                    typeof data['error'] ===
                                                    'string'
                                                        ? data['error']
                                                        : 'Upload failed';
                                                this.alert.failedMessage(msg);
                                                opts.onError(data['error']);
                                                sub.unsubscribe();
                                                finish(() =>
                                                    reject(data['error'])
                                                );
                                            });
                                            return;
                                        }

                                        const explicitFail =
                                            data['success'] === false ||
                                            data['ok'] === false;
                                        if (explicitFail) {
                                            const msg =
                                                String(
                                                    data['message'] ??
                                                        data['error'] ??
                                                        'Upload failed'
                                                ).trim() || 'Upload failed';
                                            this.zone.run(() => {
                                                opts.onFinalizingChange(false);
                                                this.alert.failedMessage(msg);
                                                opts.onError(msg);
                                                sub.unsubscribe();
                                                finish(() => reject(msg));
                                            });
                                            return;
                                        }

                                        /**
                                         * Resolve only after the progress stream reports a definitive success:
                                         * `success: true` / `ok: true`, or legacy payloads with `done` but no `success` field.
                                         * (Avoid treating `done` alone as success when `success: false`.)
                                         */
                                        const uploadSucceeded =
                                            data['success'] === true ||
                                            data['ok'] === true ||
                                            (Boolean(data['done']) &&
                                                !(
                                                    'success' in data &&
                                                    data['success'] !== true
                                                ));

                                        if (uploadSucceeded) {
                                            this.zone.run(() => {
                                                opts.onFinalizingChange(false);
                                                const info = doneInfo();
                                                opts.onDone(info);
                                                sub.unsubscribe();
                                                finish(() => resolve(info));
                                            });
                                        }
                                    },
                                    error: (err) => {
                                        this.zone.run(() => {
                                            opts.onFinalizingChange(false);
                                            this.alert.failedMessage(
                                                'Upload connection error'
                                            );
                                            opts.onError(err);
                                            sub.unsubscribe();
                                            finish(() =>
                                                reject(
                                                    err ??
                                                        new Error(
                                                            'Upload connection error'
                                                        )
                                                )
                                            );
                                        });
                                    },
                                    complete: () => {
                                        this.zone.run(() => {
                                            opts.onFinalizingChange(false);
                                            sub.unsubscribe();
                                            finish(() =>
                                                reject(
                                                    new Error(
                                                        'Upload stream ended before success'
                                                    )
                                                )
                                            );
                                        });
                                    },
                                });
                        });
                    },
                    error: (e) => {
                        // `postFormData` reads the body as text, so a rejected request (e.g. the
                        // storage limit) carries its message as a JSON string, not an object.
                        let message = '';
                        try {
                            const body = typeof e?.error === 'string' ? JSON.parse(e.error) : e?.error;
                            message = String(body?.message ?? '').trim();
                        } catch {}
                        // The caller alerts with `e.error.message || e.message`, so hand it the
                        // backend's text instead of the generic "Http failure response" wording.
                        const failure = message ? new Error(message) : e;
                        opts.onError(failure);
                        finish(() => reject(failure));
                    },
                });
        });
    }
}
